import { Invoice, InvoiceSortValue } from "@common/types/invoice";
import { InvoiceColumn } from "@common/types/views/invoiceViews";
import { ViewConfig } from "@lib/views";
import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
    SCHEDULE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { DateCell } from "./cells/DateCell";
import { LockCell } from "./cells/LockCell";
import { StatusCell } from "./cells/StatusCell";
import { CustomerCell } from "./cells/CustomerCell";
import { AccountCell } from "./cells/AccountCell";
import { AmountCell } from "./cells/AmountCell";
import { OpenAmountCell } from "./cells/OpenAmountCell";
import { PaidAmountCell } from "./cells/PaidAmountCell";
import { DaysOverdueCell } from "./cells/DaysOverdueCell";
import { EntityCell } from "./cells/EntityCell";
import { ExternalLinkCell } from "./cells/ExternalLinkCell";
import { SelectCell } from "./cells/SelectCell";
import { ScheduleCell } from "./cells/ScheduleCell";
import { InvoiceNumberCell } from "./cells/InvoiceNumberCell";
import { TermsCell } from "./cells/TermsCell";
import { SentAtCell } from "./cells/SentAtCell";
import { SentByCell } from "./cells/SentByCell";
import { InvoiceMatchCell } from "./cells/InvoiceMatchCell";

interface CreateInvoicesViewConfigParams {
    isSchedulesEnabled?: boolean;
}

export const createInvoicesViewConfig = ({
    isSchedulesEnabled = false,
}: CreateInvoicesViewConfigParams = {}): ViewConfig<
    Invoice,
    InvoiceColumn,
    InvoiceSortValue
> => ({
    columns: {
        [InvoiceColumn.LOCK]: {
            accessorKey: "id",
            name: "Lock",
            header: "",
            cell: LockCell,
            immutable: true,
            hiddenFromSettings: true,
            minSize: 32,
            maxSize: 32,
        },
        [InvoiceColumn.SCHEDULE]: {
            accessorKey: "schedule.scheduleName",
            name: "Recognition schedule",
            header: "Recognition schedule",
            cell: ScheduleCell,
            ...SCHEDULE_COLUMN_SIZE,
            internal: !isSchedulesEnabled,
        },
        [InvoiceColumn.DATE]: {
            accessorKey: "createdAt",
            name: "Created on",
            header: "Created on",
            cell: DateCell,
            sort: {
                asc: InvoiceSortValue.DATE_ASC,
                desc: InvoiceSortValue.DATE_DESC,
            },
            ...DATE_COLUMN_SIZE,
        },
        [InvoiceColumn.INVOICE_DATE]: {
            accessorKey: "invoiceDate",
            name: "Invoice date",
            header: "Invoice date",
            cell: DateCell,
            sort: {
                asc: InvoiceSortValue.INVOICE_DATE_ASC,
                desc: InvoiceSortValue.INVOICE_DATE_DESC,
            },
            ...DATE_COLUMN_SIZE,
        },
        [InvoiceColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "",
            cell: StatusCell,
            sort: {
                asc: InvoiceSortValue.STATUS_ASC,
                desc: InvoiceSortValue.STATUS_DESC,
            },
            minSize: 24,
            maxSize: 24,
        },
        [InvoiceColumn.EXTERNAL_STATUS]: {
            accessorKey: "externalStatus",
            name: "External status",
            header: "",
            cell: StatusCell,
            sort: {
                asc: InvoiceSortValue.EXTERNAL_STATUS_ASC,
                desc: InvoiceSortValue.EXTERNAL_STATUS_DESC,
            },
            minSize: 24,
            maxSize: 24,
        },
        [InvoiceColumn.INVOICE_NUMBER]: {
            accessorKey: "invoiceNumber",
            name: "Invoice Number",
            header: "Invoice Number",
            cell: InvoiceNumberCell,
            sort: {
                asc: InvoiceSortValue.INVOICE_NUMBER_ASC,
                desc: InvoiceSortValue.INVOICE_NUMBER_DESC,
            },
            minSize: 120,
            maxSize: 120,
        },
        [InvoiceColumn.CUSTOMER]: {
            accessorKey: "counterparty",
            name: "Customer",
            header: "Customer",
            cell: CustomerCell,
            sort: {
                asc: InvoiceSortValue.COUNTERPARTY_ASC,
                desc: InvoiceSortValue.COUNTERPARTY_DESC,
            },
            minSize: 200,
            maxSize: 400,
        },
        [InvoiceColumn.ACCOUNT]: {
            accessorKey: "financialAccount",
            name: "Account",
            header: "Account",
            cell: AccountCell,
            sort: {
                asc: InvoiceSortValue.ACCOUNT_ASC,
                desc: InvoiceSortValue.ACCOUNT_DESC,
            },
            minSize: 120,
            maxSize: 180,
        },
        [InvoiceColumn.DUE_DATE]: {
            accessorKey: "dueDateOnly",
            name: "Due date",
            header: "Due date",
            cell: DateCell,
            sort: {
                asc: InvoiceSortValue.DUE_DATE_ASC,
                desc: InvoiceSortValue.DUE_DATE_DESC,
            },
            ...DATE_COLUMN_SIZE,
        },
        [InvoiceColumn.TERMS]: {
            accessorKey: "termType",
            name: "Terms",
            header: "Terms",
            cell: TermsCell,
            minSize: 100,
            maxSize: 160,
        },
        [InvoiceColumn.SENT_AT]: {
            accessorKey: "sentInfo",
            name: "Sent at",
            header: "Sent at",
            cell: SentAtCell,
            ...DATE_COLUMN_SIZE,
        },
        [InvoiceColumn.SENT_BY]: {
            accessorKey: "sentInfo",
            name: "Sent by",
            header: "Sent by",
            cell: SentByCell,
            minSize: 120,
            maxSize: 200,
        },
        [InvoiceColumn.AMOUNT]: {
            accessorKey: "total",
            name: "Amount",
            header: "Amount",
            cell: AmountCell,
            sort: {
                asc: InvoiceSortValue.AMOUNT_ASC,
                desc: InvoiceSortValue.AMOUNT_DESC,
            },
            ...AMOUNT_COLUMN_SIZE,
        },
        [InvoiceColumn.OPEN]: {
            accessorKey: "total",
            name: "Open",
            header: "Open",
            cell: OpenAmountCell,
            sort: {
                asc: InvoiceSortValue.OPEN_ASC,
                desc: InvoiceSortValue.OPEN_DESC,
            },
            ...AMOUNT_COLUMN_SIZE,
        },
        [InvoiceColumn.PAID]: {
            accessorKey: "total",
            name: "Paid",
            header: "Paid",
            cell: PaidAmountCell,
            sort: {
                asc: InvoiceSortValue.PAID_ASC,
                desc: InvoiceSortValue.PAID_DESC,
            },
            align: "right",
            ...AMOUNT_COLUMN_SIZE,
        },
        [InvoiceColumn.DAYS_OVERDUE]: {
            accessorKey: "dueDateOnly",
            name: "Days overdue",
            header: "Days overdue",
            cell: DaysOverdueCell,
            sort: {
                asc: InvoiceSortValue.DAYS_OVERDUE_ASC,
                desc: InvoiceSortValue.DAYS_OVERDUE_DESC,
            },
            align: "right",
            minSize: 120,
            maxSize: 140,
        },
        [InvoiceColumn.MATCH]: {
            accessorKey: "id",
            name: "Match",
            header: "Match",
            cell: InvoiceMatchCell,
            minSize: 140,
            maxSize: 220,
        },
        [InvoiceColumn.ENTITY]: {
            accessorKey: "financialAccount.entity",
            name: "Entity",
            header: "",
            cell: EntityCell,
            minSize: 48,
            maxSize: 48,
        },
        [InvoiceColumn.EXTERNAL_LINK]: {
            accessorKey: "id",
            name: "View",
            header: "",
            cell: ExternalLinkCell,
            immutable: true,
            hiddenFromSettings: true,
            minSize: 48,
            maxSize: 48,
        },
        [InvoiceColumn.SELECT]: {
            accessorKey: "id",
            name: "Select",
            header: "",
            cell: SelectCell,
            immutable: true,
            hiddenFromSettings: true,
            minSize: 88,
            maxSize: 88,
        },
    },
});
